import React from 'react';
import { ChevronUp, Minus, Monitor, Plus, Search, Smartphone, X } from 'lucide-react';
import {
  APP_CAPABILITY_PERMS,
  WEB_CAPABILITY_PERMS,
  listCapabilityDeviations,
  type CapabilityMap,
  type PlatformAccess,
} from '../capabilitiesUi';
import { GEOFENCE_CAPABILITY_KEYS } from '../../../lib/capabilities/geofence';
import { isAdminLockedCapability } from '../../../lib/capabilities/adminRole';
import { SETTINGS_INPUT_CLASS } from '../utils/settingsSectionStyles';
import { useModuleAccess } from '../../../hooks/useModuleAccess';
import { availableRoleEditorPermKeys } from '../roleEditorGroups';

type CapabilityDeviationEditorProps = {
  presetCaps: CapabilityMap;
  overrides: CapabilityMap;
  access: PlatformAccess;
  /** Admins always keep the settings rights — those can't be taken away here. */
  isAdmin?: boolean;
  /** Owner: web is always full, only app rights can deviate. */
  appOnly?: boolean;
  onChange: (next: CapabilityMap) => void;
};

export function CapabilityDeviationEditor({
  presetCaps,
  overrides,
  access,
  isAdmin = false,
  appOnly = false,
  onChange,
}: CapabilityDeviationEditorProps) {
  // Wie Rollen & Rechte: Rechte abgewählter Module fehlen, App-Rechte nur mit Mobile-App-Modul.
  const { modules, isEnabled } = useModuleAccess();
  const availableKeys = React.useMemo(() => availableRoleEditorPermKeys(modules), [modules]);
  const appAvailable = isEnabled('field_app');
  const deviations = listCapabilityDeviations(presetCaps, overrides).filter(
    (entry) => availableKeys.has(entry.key) && (!appOnly || entry.key.startsWith('app.')),
  );
  const [open, setOpen] = React.useState(deviations.length > 0);
  const [selectedTab, setTab] = React.useState<'web' | 'app'>(access === 'field_only' || appOnly ? 'app' : 'web');
  const tab = appAvailable ? selectedTab : 'web';
  const webTabVisible = !appOnly;
  const [adding, setAdding] = React.useState(false);
  const [search, setSearch] = React.useState('');

  if (appOnly && !appAvailable) return null;

  const visibleDeviations = deviations.filter((entry) => entry.key.startsWith(tab === 'web' ? 'web.' : 'app.'));

  const availablePerms = (tab === 'web' ? WEB_CAPABILITY_PERMS : APP_CAPABILITY_PERMS).filter((perm) => {
    if (!availableKeys.has(perm.key)) return false;
    if (isAdmin && isAdminLockedCapability(perm.key)) return false;
    if (access === 'field_only' && perm.key.startsWith('web.')) return false;
    if (typeof overrides[perm.key] === 'boolean') return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return `${perm.groupLabel} ${perm.label}`.toLowerCase().includes(q);
  });

  const resetDeviation = (key: string) => {
    const next = { ...overrides };
    delete next[key];
    onChange(next);
  };

  const addDeviation = (key: string) => {
    const presetValue = Boolean(presetCaps[key]);
    const next = { ...overrides, [key]: !presetValue };

    // If view is explicitly removed, dependent write-style rights must also be removed,
    // otherwise shared implications would turn view right back on via the preset's edit flag.
    if (presetValue && key.endsWith('.view')) {
      const editKey = `${key.slice(0, -5)}.edit`;
      if (editKey in presetCaps || editKey in overrides) next[editKey] = false;
      if (key === 'web.hr.view') {
        next['web.hr.approve'] = false;
        next['web.hr.internal'] = false;
      }
      if (key === 'web.rechnungen.view') {
        next['web.rechnungen.approve'] = false;
        next['web.rechnungen.datev'] = false;
      }
    }
    // Same for geofencing, which only exists on top of clock editing.
    if (presetValue && (key === 'app.zeit.view' || key === 'app.zeit.edit')) {
      for (const geofenceKey of GEOFENCE_CAPABILITY_KEYS) {
        if (geofenceKey in presetCaps || geofenceKey in overrides) next[geofenceKey] = false;
      }
    }

    onChange(next);
    setSearch('');
    setAdding(false);
  };

  return (
    <div className="rounded-xl border border-app-border bg-app-surface">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronUp className="h-3.5 w-3.5 text-slate-400" /> : <ChevronUp className="h-3.5 w-3.5 rotate-180 text-slate-400" />}
          <span className="text-sm font-semibold text-app-text">Individuelle Abweichungen</span>
          {deviations.length > 0 ? (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700">
              {deviations.length} angepasst
            </span>
          ) : null}
        </div>
        <div className="text-xs text-slate-400">Expertenmodus</div>
      </button>

      {open ? (
        <div className="border-t border-app-border px-4 pb-4">
          <div className="mb-3 mt-3 inline-flex rounded-lg border-b border-app-border bg-transparent p-0">
            {webTabVisible ? (
            <button
              type="button"
              onClick={() => setTab('web')}
              className={`inline-flex items-center gap-1.5 border-b-2 px-2 py-1.5 text-xs font-medium ${
                tab === 'web' ? 'border-pipedrive-blue text-pipedrive-blue' : 'border-transparent text-app-text-subtle'
              }`}
            >
              <Monitor className="h-3.5 w-3.5" />
              Web
            </button>
            ) : null}
            {appAvailable ? (
            <button
              type="button"
              onClick={() => setTab('app')}
              className={`inline-flex items-center gap-1.5 border-b-2 px-2 py-1.5 text-xs font-medium ${
                tab === 'app' ? 'border-pipedrive-blue text-pipedrive-blue' : 'border-transparent text-app-text-subtle'
              }`}
            >
              <Smartphone className="h-3.5 w-3.5" />
              App
            </button>
            ) : null}
          </div>

          <div className="space-y-2">
            {visibleDeviations.length === 0 ? (
              <p className="text-xs text-slate-400">Keine Abweichungen in diesem Bereich.</p>
            ) : (
              visibleDeviations.map((entry) => (
                <div key={entry.key} className="flex items-center gap-2 rounded-lg border border-app-border bg-app-surface-muted px-3 py-2">
                  <span
                    className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                      entry.kind === 'allow' ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'
                    }`}
                  >
                    {entry.kind === 'allow' ? <Plus className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
                  </span>
                  <div className="min-w-0 flex-1 text-sm text-app-text-muted">
                    <span className="font-medium">{entry.label.split(' · ')[0]}</span>
                    <span className="text-app-text-subtle">
                      {' '}
                      · {entry.label.split(' · ')[1] ?? ''} {entry.kind === 'allow' ? 'zusätzlich erlaubt' : 'entzogen'}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => resetDeviation(entry.key)}
                    className="rounded p-1 text-slate-400 hover:bg-app-surface hover:text-app-text-muted"
                    aria-label="Abweichung zurücksetzen"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))
            )}
          </div>

          {adding ? (
            <div className="mt-3 rounded-lg border border-app-border bg-app-surface p-2">
              <div className="mb-2 relative">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Recht suchen…"
                  className={`${SETTINGS_INPUT_CLASS} pl-7`}
                />
              </div>
              <div className="max-h-40 space-y-1 overflow-y-auto app-scrollbar">
                {availablePerms.length === 0 ? (
                  <p className="px-2 py-2 text-xs text-slate-400">Keine weiteren Rechte zum Hinzufugen gefunden.</p>
                ) : (
                  availablePerms.map((perm) => (
                    <button
                      key={perm.key}
                      type="button"
                      onClick={() => addDeviation(perm.key)}
                      className="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm hover:bg-app-hover"
                    >
                      <span>{perm.groupLabel} · {perm.label}</span>
                      {Boolean(presetCaps[perm.key]) ? (
                        <Minus className="h-3.5 w-3.5 text-red-500" />
                      ) : (
                        <Plus className="h-3.5 w-3.5 text-pipedrive-blue" />
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-pipedrive-blue hover:underline"
            >
              <Plus className="h-4 w-4" />
              Abweichung hinzufügen
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
}
